import { useEffect, useMemo, useRef, useState } from 'react';
import {
  LayoutGrid,
  List,
  Soup,
  UtensilsCrossed,
  Croissant,
  IceCreamCone,
  CupSoda,
  Utensils,
  Coffee,
  Salad,
  Beef,
  Wheat,
  Pizza,
  Sandwich,
  CakeSlice,
  Wine,
  LayoutGrid as AllIcon,
  Search,
  X,
  Layers,
  type LucideIcon
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { screenService, tryUnprotectParam, type CustomerHomeScreen, type QrMenuScreen, type MenuItem } from '@kss/api';
import { Card, Chip, VegMark, Button, PriceTag, ItemDetailModal, useCart, useToast, useSearch, useTable, setTable, cn, GardenLoader, applyGuard } from '@kss/ui';
import { useT } from '../i18n/i18n';

/** Category code → icon (falls back to a generic utensils glyph for unknown ids). */
const CATEGORY_ICON: Record<string, LucideIcon> = {
  breakfast: Coffee,
  soups: Soup,
  'starters-veg': Salad,
  'starters-nonveg': Beef,
  'maincourse-veg': UtensilsCrossed,
  'maincourse-nonveg': Beef,
  rice: Wheat,
  breads: Croissant,
  chinese: Soup,
  pizza: Pizza,
  burgers: Sandwich,
  sandwiches: Sandwich,
  pasta: Wheat,
  salads: Salad,
  desserts: CakeSlice,
  beverages: CupSoda,
  icecream: IceCreamCone,
  mocktails: Wine,
  // legacy short codes (backward compatible)
  starters: Salad,
  mains: UtensilsCrossed
};
const iconFor = (id: string): LucideIcon => CATEGORY_ICON[id] ?? Utensils;

type Filter = 'all' | 'veg' | 'nonveg' | 'bestseller';
type View = 'grid' | 'list';

export default function Home() {
  const [home, setHome] = useState<CustomerHomeScreen | null>(null);
  const [menu, setMenu] = useState<QrMenuScreen | null>(null);
  const [activeCat, setActiveCat] = useState('all'); // 'all' | category id (scroll-spy driven)
  const { query, setQuery } = useSearch(); // shared with the top-bar search box
  const [acOpen, setAcOpen] = useState(false); // autocomplete popup
  const [catSheet, setCatSheet] = useState(false); // floating category index sheet
  const [filter, setFilter] = useState<Filter>('all');
  const [view, setView] = useState<View>('grid');
  const [selected, setSelected] = useState<MenuItem | null>(null);
  const [heroIdx, setHeroIdx] = useState(0); // active hero slide (auto-advances every 5s, matches Flutter)
  const cart = useCart();
  const toast = useToast();
  const t = useT();
  const table = useTable(); // current dine-in table (from a scanned QR / URL)
  const [searchParams, setSearchParams] = useSearchParams();

  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const suppressSpy = useRef(false);

  // A QR that lands on Home as "/?t=<token>" — capture the table, persist it, strip the param.
  //
  // The token is only adopted if it actually DECRYPTS to a table code. This path used to take
  // whatever came back, and `unprotectParam` reports a token it can't read by returning the
  // token — so a stale or mistyped QR stored "enc.…" as the table. That is not a table code:
  // the header rendered it as "T713157318113", POST /api/order and POST /api/bill/request both
  // 400'd on the table-code pattern, and POST /api/auth/guest 401'd with "that table isn't
  // available". It survived reloads, so the customer was stuck until site data was cleared.
  // A token we can't read now leaves them a takeaway, which works.
  useEffect(() => {
    const token = searchParams.get('t');
    if (!token) return;
    tryUnprotectParam(token).then((v) => {
      if (v) setTable(v.toLowerCase());
    });
    const next = new URLSearchParams(searchParams);
    next.delete('t');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Both fetches had no .catch, so an API failure was an unhandled rejection and the
  // customer sat on "Loading the menu…" forever with no error and no retry.
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
    Promise.all([screenService.getCustomerHome(), screenService.getQrMenu(table)])
      .then(([h, m]) => {
        setHome(h);
        setMenu(m);
      })
      .catch(() => setFailed(true));
  }, [table]);

  const categories = useMemo(() => menu?.categories ?? [], [menu]);
  const allItems = useMemo(() => menu?.items ?? [], [menu]);

  // Hero slider — the promo banner followed by up to 3 best-seller dishes (mirrors the Flutter carousel).
  const heroSlides = useMemo(() => {
    if (!home) return [] as { title: string; subtitle: string; tag: string; image: string }[];
    const slides = [{ title: home.hero.title, subtitle: home.hero.subtitle, tag: `USE ${home.hero.code}`, image: home.hero.image }];
    for (const m of allItems.filter((m) => m.bestseller && m.image).slice(0, 3)) {
      slides.push({ title: m.name, subtitle: `Chef's pick · ₹${Math.round(m.price)}`, tag: 'BESTSELLER', image: m.image });
    }
    return slides;
  }, [home, allItems]);

  // Auto-advance the hero every 5 seconds (only when there's more than one slide).
  useEffect(() => {
    if (heroSlides.length < 2) return;
    const id = setInterval(() => setHeroIdx((i) => (i + 1) % heroSlides.length), 5000);
    return () => clearInterval(id);
  }, [heroSlides.length]);

  // Keep the active index in range if the slide list shrinks.
  useEffect(() => {
    if (heroIdx >= heroSlides.length && heroSlides.length > 0) setHeroIdx(0);
  }, [heroSlides.length, heroIdx]);

  // Grouped, category-wise sections after applying the veg + text-search filters.
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = (m: MenuItem) => {
      if (filter === 'veg' && !m.veg) return false;
      if (filter === 'nonveg' && m.veg) return false;
      if (filter === 'bestseller' && !m.bestseller) return false;
      if (q && !(m.name.toLowerCase().includes(q) || (m.desc ?? '').toLowerCase().includes(q))) return false;
      return true;
    };
    return categories
      .map((c) => ({ cat: c, items: allItems.filter((m) => m.category === c.id && matches(m)) }))
      .filter((g) => g.items.length > 0);
  }, [categories, allItems, filter, query]);

  const totalMatches = useMemo(() => groups.reduce((n, g) => n + g.items.length, 0), [groups]);

  // Autocomplete suggestions for the sticky search box (top matches across all sections).
  const acMatches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [] as MenuItem[];
    return allItems
      .filter((m) => m.name.toLowerCase().includes(q) || (m.desc ?? '').toLowerCase().includes(q))
      .slice(0, 7);
  }, [allItems, query]);

  // Scroll-spy — highlight the category chip of the section currently in view.
  useEffect(() => {
    const onScroll = () => {
      if (suppressSpy.current) return;
      const threshold = 200;
      let active = 'all';
      for (const g of groups) {
        const el = sectionRefs.current[g.cat.id];
        if (el && el.getBoundingClientRect().top <= threshold) active = g.cat.id;
      }
      setActiveCat(active);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [groups]);

  function scrollToCat(id: string) {
    suppressSpy.current = true;
    setActiveCat(id);
    const el = id === 'all' ? null : sectionRefs.current[id];
    // scrollIntoView honours the sections' scroll-mt-28 (clears the sticky header) and,
    // unlike window.scrollTo({behavior:'smooth'}), works reliably across browsers.
    if (el) el.scrollIntoView({ block: 'start' });
    else window.scrollTo({ top: 0 });
    window.setTimeout(() => {
      suppressSpy.current = false;
    }, 700);
  }

  function addToCart(m: MenuItem) {
    cart.add({ id: m.id, name: m.name, price: m.price, veg: m.veg, image: m.image });
    toast.show(`${m.name} added`, 'success');
    cart.openCart();
  }

  if (!home || !menu) {
    if (failed) {
      return (
        <div className='px-4 py-24 text-center'>
          <div className='font-display text-[15px] font-bold'>We couldn’t load the menu</div>
          <p className='mx-auto mt-1.5 max-w-[300px] text-[12.5px] leading-relaxed text-content-muted'>
            Check your connection and try again.
          </p>
          <Button className='mt-4' onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      );
    }
    return <GardenLoader full={false} label='Loading the menu…' />;
  }

  return (
    <div className='mx-auto max-w-[1200px] px-4 py-5 md:px-6'>
      {/* Hero promo — auto-advancing slider (5s), matches the Flutter carousel */}
      <div className='relative h-[170px] overflow-hidden rounded-xl md:h-[190px]'>
        {heroSlides.map((s, i) => (
          <div
            key={i}
            className='absolute inset-0 flex flex-col justify-center px-6 text-white transition-opacity duration-700 ease-in-out'
            style={{
              opacity: i === heroIdx ? 1 : 0,
              backgroundImage: `linear-gradient(90deg, rgba(12,16,21,.82) 26%, rgba(12,16,21,.12)), url(${s.image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            <div className='font-display text-2xl font-extrabold leading-tight md:text-[28px]'>
              {s.title}
              <br />
              {s.subtitle}
            </div>
            <span className='mt-2.5 w-fit rounded-md bg-primary px-3 py-1.5 font-display text-[10.5px] font-extrabold'>
              {s.tag}
            </span>
          </div>
        ))}
        {/* Page dots */}
        {heroSlides.length > 1 && (
          <div className='absolute bottom-3 left-6 flex gap-1.5'>
            {heroSlides.map((_, i) => (
              <button
                key={i}
                aria-label={`Go to slide ${i + 1}`}
                onClick={() => setHeroIdx(i)}
                className={cn(
                  'h-1.5 rounded-full transition-all',
                  i === heroIdx ? 'w-5 bg-white' : 'w-1.5 bg-white/45'
                )}
              />
            ))}
          </div>
        )}
      </div>

      {/* Sticky search + category rail — stays visible while scrolling the menu. */}
      <div className='sticky top-0 z-20 -mx-4 mt-4 bg-surface-content/95 px-4 py-2 backdrop-blur md:-mx-6 md:px-6'>
        {/* Search with autocomplete popup */}
        <div className='relative mb-2'>
          <div className='flex items-center gap-2 rounded-lg border border-border bg-surface-card px-3 py-2'>
            <Search size={15} className='text-content-muted' />
            <input
              value={query}
              onChange={(e) => {
                // A search term is still user input that reaches the API — capped and stripped of
                // markup, with a paste treated exactly like typing.
                setQuery(applyGuard(e.target.value, 'text', 80));
                setAcOpen(true);
              }}
              onFocus={() => setAcOpen(true)}
              onKeyDown={(e) => e.key === 'Escape' && setAcOpen(false)}
              maxLength={80}
              aria-label='Search dishes'
              placeholder='Search dishes…'
              className='flex-1 bg-transparent text-[13px] text-content-primary placeholder:text-content-muted focus:outline-none'
            />
            {query && (
              <button onClick={() => { setQuery(''); setAcOpen(false); }} aria-label='Clear search'>
                <X size={15} className='text-content-muted' />
              </button>
            )}
          </div>
          {acOpen && acMatches.length > 0 && (
            <>
              <button className='fixed inset-0 z-10 cursor-default' aria-label='Close suggestions' onClick={() => setAcOpen(false)} />
              <div className='absolute inset-x-0 top-full z-20 mt-1 max-h-[340px] overflow-y-auto rounded-xl border border-border bg-surface-card shadow-pop'>
                {acMatches.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => { setAcOpen(false); setSelected(m); }}
                    className='flex w-full items-center gap-3 border-b border-border px-3 py-2 text-left last:border-0 hover:bg-surface-card-hover'
                  >
                    <div className='h-9 w-9 flex-shrink-0 rounded-md bg-cover bg-center' style={{ backgroundImage: `url(${m.image})` }} />
                    <div className='min-w-0 flex-1'>
                      <div className='flex items-center gap-1.5 truncate text-[12.5px] font-semibold'>
                        <VegMark veg={m.veg} bare /> {m.name}
                      </div>
                      <div className='truncate text-[10.5px] text-content-muted'>{m.desc}</div>
                    </div>
                    <PriceTag price={m.price} mrp={m.mrp} />
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        <div className='-mx-1 flex items-center gap-2 overflow-x-auto px-1'>
          <CatChip icon={AllIcon} label={t('all')} active={activeCat === 'all'} onClick={() => scrollToCat('all')} />
          {categories.map((c) => (
            <CatChip
              key={c.id}
              icon={iconFor(c.id)}
              label={c.name}
              active={activeCat === c.id}
              onClick={() => scrollToCat(c.id)}
            />
          ))}
        </div>
      </div>

      {/* Filters · view toggle */}
      <div className='mb-1 mt-3 flex flex-wrap items-center gap-2'>
        <Chip active={filter === 'all'} onClick={() => setFilter('all')}>
          {t('all')}
        </Chip>
        <Chip active={filter === 'veg'} onClick={() => setFilter('veg')}>
          <VegMark veg bare /> {t('veg')}
        </Chip>
        <Chip active={filter === 'nonveg'} onClick={() => setFilter('nonveg')}>
          <VegMark veg={false} bare /> {t('nonVeg')}
        </Chip>
        <Chip active={filter === 'bestseller'} onClick={() => setFilter('bestseller')}>
          ★ {t('bestseller')}
        </Chip>
        <div className='ml-auto flex items-center rounded-lg border border-border p-0.5'>
          <button
            onClick={() => setView('grid')}
            aria-label='Grid view'
            className={cn(
              'flex h-7 w-7 items-center justify-center rounded-md transition',
              view === 'grid' ? 'bg-primary text-primary-foreground' : 'text-content-secondary'
            )}
          >
            <LayoutGrid size={15} />
          </button>
          <button
            onClick={() => setView('list')}
            aria-label='List view'
            className={cn(
              'flex h-7 w-7 items-center justify-center rounded-md transition',
              view === 'list' ? 'bg-primary text-primary-foreground' : 'text-content-secondary'
            )}
          >
            <List size={15} />
          </button>
        </div>
      </div>

      {/* Grouped, category-wise menu sections */}
      {totalMatches === 0 ? (
        <div className='py-16 text-center text-sm text-content-muted'>
          {query ? `No dishes match “${query}”.` : 'No dishes here yet.'}
        </div>
      ) : (
        groups.map((g) => {
          const Icon = iconFor(g.cat.id);
          return (
            <section
              key={g.cat.id}
              ref={(el) => {
                sectionRefs.current[g.cat.id] = el;
              }}
              className='scroll-mt-28 pt-4'
            >
              <h2 className='mb-3 flex items-center gap-2 font-display text-[15px] font-bold'>
                <Icon size={17} className='text-primary' /> {g.cat.name}
                <span className='text-[11px] font-semibold text-content-muted'>{g.items.length}</span>
              </h2>
              {view === 'grid' ? (
                <div className='grid grid-cols-2 gap-3 md:grid-cols-4'>
                  {g.items.map((m) => (
                    <Card key={m.id} className='flex flex-col overflow-hidden'>
                      <div
                        onClick={() => setSelected(m)}
                        className='h-[150px] w-full flex-shrink-0 cursor-pointer'
                        style={{ backgroundImage: `url(${m.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
                      />
                      <div className='flex flex-1 flex-col px-3 py-3'>
                        <div onClick={() => setSelected(m)} className='cursor-pointer'>
                          <div className='flex items-center gap-1.5 font-display text-[13px] font-bold'>
                            <VegMark veg={m.veg} bare />
                            <span className='min-w-0 truncate'>{m.name}</span>
                          </div>
                          <div className='mt-0.5 truncate text-[11px] text-content-muted'>{m.desc}</div>
                        </div>
                        <div className='mt-auto flex items-end justify-between gap-2 pt-3'>
                          <PriceTag price={m.price} mrp={m.mrp} />
                          <Button variant='outline' size='sm' onClick={() => addToCart(m)}>
                            {t('addToCart')}
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className='flex flex-col gap-2.5'>
                  {g.items.map((m) => (
                    <Card key={m.id} className='flex items-center gap-3 p-3'>
                      <div
                        onClick={() => setSelected(m)}
                        className='flex min-w-0 flex-1 cursor-pointer items-center gap-3'
                      >
                        <div
                          className='h-16 w-16 flex-shrink-0 rounded-xl'
                          style={{ backgroundImage: `url(${m.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
                        />
                        <div className='min-w-0 flex-1'>
                          <div className='flex min-w-0 items-center gap-1.5 font-display text-[13px] font-bold'>
                            <VegMark veg={m.veg} bare />
                            <span className='truncate'>{m.name}</span>
                          </div>
                          <div className='mt-0.5 truncate text-[10.5px] text-content-muted'>{m.desc}</div>
                          <PriceTag price={m.price} mrp={m.mrp} className='mt-1' />
                        </div>
                      </div>
                      <Button variant='outline' size='sm' onClick={() => addToCart(m)}>
                        {t('addToCart')}
                      </Button>
                    </Card>
                  ))}
                </div>
              )}
            </section>
          );
        })
      )}

      {/* Floating category button — jump to any section (focus, doesn't filter). */}
      {groups.length > 0 && !catSheet && (
        <button
          onClick={() => setCatSheet(true)}
          className='fixed bottom-[68px] left-1/2 z-40 flex -translate-x-1/2 items-center gap-2 rounded-full bg-content-primary px-4 py-2.5 font-display text-[12.5px] font-bold text-surface-content shadow-pop'
        >
          <Layers size={15} />
          {activeCat === 'all' ? 'Menu' : (categories.find((c) => c.id === activeCat)?.name ?? 'Menu')}
          <span className='opacity-70'>· {groups.length}</span>
        </button>
      )}
      {catSheet && (
        <div className='fixed inset-0 z-50 flex flex-col justify-end'>
          <button className='absolute inset-0 bg-black/40' aria-label='Close' onClick={() => setCatSheet(false)} />
          <div className='relative max-h-[72vh] overflow-y-auto rounded-t-2xl border-t border-border bg-surface-card p-4 shadow-pop'>
            <div className='mx-auto mb-3 h-1 w-10 rounded-full bg-border' />
            <div className='mb-2.5 flex items-center justify-between'>
              <div className='font-display text-sm font-bold'>Jump to category</div>
              <button onClick={() => setCatSheet(false)} aria-label='Close'>
                <X size={18} className='text-content-muted' />
              </button>
            </div>
            <div className='grid grid-cols-2 gap-2'>
              {groups.map((g) => {
                const Icon = iconFor(g.cat.id);
                return (
                  <button
                    key={g.cat.id}
                    onClick={() => { setCatSheet(false); scrollToCat(g.cat.id); }}
                    className={cn(
                      'flex items-center gap-2 rounded-lg border px-3 py-2.5 text-left text-[12.5px] font-semibold transition',
                      activeCat === g.cat.id ? 'border-primary bg-primary-light text-primary' : 'border-border text-content-secondary hover:border-primary'
                    )}
                  >
                    <Icon size={15} className='flex-shrink-0' />
                    <span className='min-w-0 flex-1 truncate'>{g.cat.name}</span>
                    <span className='text-[11px] text-content-muted'>{g.items.length}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <ItemDetailModal
        item={selected}
        onClose={() => setSelected(null)}
        onAdd={(qty, note) => {
          if (!selected) return;
          cart.add(
            {
              id: selected.id,
              name: selected.name,
              price: selected.price,
              veg: selected.veg,
              image: selected.image,
              note
            },
            qty
          );
          toast.show(`${selected.name} added`, 'success');
          cart.openCart();
        }}
      />
    </div>
  );
}

/** A rounded category pill with an icon (menu rail). */
function CatChip({
  icon: Icon,
  label,
  active,
  onClick
}: {
  icon: LucideIcon;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 py-1.5 text-[12.5px] font-semibold transition',
        active
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-border bg-surface-card text-content-secondary hover:border-primary hover:text-primary'
      )}
    >
      <Icon size={15} className='flex-shrink-0' /> {label}
    </button>
  );
}
